/**
 * ViewportController - Viewport state and transform management
 */

import { VIEWPORT_STORAGE_KEY } from '../constants/index.js';

// Viewport state
const viewport = {
    x: 0,
    y: 0,
    scale: 1,
    minScale: 0.25,
    maxScale: 3
};

// Module dependencies (injected via init)
let canvasEl = null;
let cablesSvgEl = null;
let renderAllCables = null;
let showToast = null;
let instanceModel = null;

// Debounce timer for saving
let viewportSaveTimeout = null;

/**
 * Initialize the viewport controller with dependencies
 * @param {Object} deps - Dependencies
 * @param {HTMLElement} deps.canvas - Canvas element
 * @param {SVGElement} deps.cablesSvg - Cables SVG element
 * @param {Function} deps.renderAllCables - Function to re-render all cables
 * @param {Function} deps.showToast - Function to show toast messages
 * @param {Object} deps.instanceModel - Instance model for device access
 */
export function initViewportController(deps) {
    canvasEl = deps.canvas;
    cablesSvgEl = deps.cablesSvg;
    renderAllCables = deps.renderAllCables;
    showToast = deps.showToast;
    instanceModel = deps.instanceModel;
}

/**
 * Get the current viewport state (read-only access)
 * @returns {{x: number, y: number, scale: number, minScale: number, maxScale: number}} Viewport state object
 */
export function getViewport() {
    return viewport;
}

/**
 * Save viewport to localStorage (debounced, 150ms delay)
 * Persists x, y, and scale values for session continuity
 */
export function saveViewport() {
    if (viewportSaveTimeout) {
        clearTimeout(viewportSaveTimeout);
    }
    viewportSaveTimeout = setTimeout(() => {
        try {
            const data = {
                x: viewport.x,
                y: viewport.y,
                scale: viewport.scale
            };
            localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('Failed to save viewport:', e);
        }
        viewportSaveTimeout = null;
    }, 150);
}

/**
 * Load viewport from localStorage
 * @returns {boolean} Whether viewport was loaded successfully
 */
export function loadViewport() {
    try {
        const saved = localStorage.getItem(VIEWPORT_STORAGE_KEY);
        if (!saved) return false;

        const data = JSON.parse(saved);

        if (typeof data.x !== 'number' || typeof data.y !== 'number' || typeof data.scale !== 'number') {
            console.warn('Invalid viewport data, using defaults');
            return false;
        }

        viewport.scale = Math.max(viewport.minScale, Math.min(viewport.maxScale, data.scale));
        viewport.x = data.x;
        viewport.y = data.y;

        return true;
    } catch (e) {
        console.warn('Failed to load viewport:', e);
        return false;
    }
}

/**
 * Apply viewport transform to canvas and cables SVG elements
 * Updates CSS transform with current x, y, scale values
 */
export function applyViewportTransform() {
    const transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale}) translateZ(0)`;
    canvasEl.style.transform = transform;
    canvasEl.style.transformOrigin = '0 0';
    cablesSvgEl.style.transform = transform;
    cablesSvgEl.style.transformOrigin = '0 0';
}

/**
 * Convert screen coordinates to canvas coordinates
 * Accounts for viewport pan and zoom
 * @param {number} screenX - X position in screen/client coordinates
 * @param {number} screenY - Y position in screen/client coordinates
 * @returns {{x: number, y: number}} Position in canvas coordinates
 */
export function screenToCanvas(screenX, screenY) {
    const container = document.querySelector('.canvas-container');
    const rect = container.getBoundingClientRect();
    const x = (screenX - rect.left - viewport.x) / viewport.scale;
    const y = (screenY - rect.top - viewport.y) / viewport.scale;
    return { x, y };
}

/**
 * Convert canvas coordinates to screen coordinates
 * Accounts for viewport pan and zoom
 * @param {number} canvasX - X position in canvas coordinates
 * @param {number} canvasY - Y position in canvas coordinates
 * @returns {{x: number, y: number}} Position in screen/client coordinates
 */
export function canvasToScreen(canvasX, canvasY) {
    const container = document.querySelector('.canvas-container');
    const rect = container.getBoundingClientRect();
    const x = canvasX * viewport.scale + viewport.x + rect.left;
    const y = canvasY * viewport.scale + viewport.y + rect.top;
    return { x, y };
}

/**
 * Zoom at a specific screen point (keeps that point stationary)
 * @param {number} screenX - X position of zoom center in screen coordinates
 * @param {number} screenY - Y position of zoom center in screen coordinates
 * @param {number} newScale - Desired scale value (will be clamped to min/max)
 */
export function zoomAtPoint(screenX, screenY, newScale) {
    const container = document.querySelector('.canvas-container');
    const rect = container.getBoundingClientRect();
    
    newScale = Math.max(viewport.minScale, Math.min(viewport.maxScale, newScale));
    
    const px = screenX - rect.left;
    const py = screenY - rect.top;
    
    const canvasX = (px - viewport.x) / viewport.scale;
    const canvasY = (py - viewport.y) / viewport.scale;
    
    viewport.scale = newScale;
    
    viewport.x = px - canvasX * viewport.scale;
    viewport.y = py - canvasY * viewport.scale;
    
    applyViewportTransform();
    renderAllCables();
}

/**
 * Handle wheel event for zooming
 * Zooms in/out centered on mouse position
 * @param {WheelEvent} e - Wheel event
 */
export function handleWheel(e) {
    e.preventDefault();
    
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = viewport.scale * zoomFactor;
    
    zoomAtPoint(e.clientX, e.clientY, newScale);
    updateZoomIndicator();
    saveViewport();
}

/**
 * Update the zoom indicator in the footer
 * Displays current zoom level as percentage
 */
export function updateZoomIndicator() {
    const indicator = document.getElementById('footer-zoom');
    if (indicator) {
        indicator.textContent = Math.round(viewport.scale * 100) + '%';
    }
}

/**
 * Reset zoom to default (1:1) and center viewport at origin
 * Shows toast notification
 */
export function resetZoom() {
    viewport.x = 0;
    viewport.y = 0;
    viewport.scale = 1;
    applyViewportTransform();
    renderAllCables();
    updateZoomIndicator();
    saveViewport();
    showToast('Zoom reset');
}

/**
 * Set viewport position directly (for panning)
 * @param {number} x - New x position
 * @param {number} y - New y position
 */
export function setViewportPosition(x, y) {
    viewport.x = x;
    viewport.y = y;
}

/**
 * Set viewport scale directly (for pinch zoom)
 * @param {number} scale - New scale value (will be clamped)
 */
export function setViewportScale(scale) {
    viewport.scale = Math.max(viewport.minScale, Math.min(viewport.maxScale, scale));
}

/**
 * Toggle between 100% zoom and fit-all-devices view
 * If at ~100%, switches to fit all devices on screen
 * If zoomed out, switches to 100% centered on patch
 */
export function toggleFitPatch() {
    const devices = instanceModel.getAll();
    
    // If no devices, just reset to default view
    if (devices.length === 0) {
        resetZoom();
        return;
    }
    
    // Check if we're currently at ~100% zoom (within 1% tolerance)
    const isAt100 = Math.abs(viewport.scale - 1.0) < 0.01;
    
    if (isAt100) {
        // Switch to "see whole patch" view
        fitAllDevices();
    } else {
        // Switch to 100% zoom, centered on the patch
        const container = document.querySelector('.canvas-container');
        const containerRect = container.getBoundingClientRect();
        
        // Find center of all devices
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        devices.forEach(d => {
            const el = document.getElementById(d.id);
            if (!el) return;
            const width = el.offsetWidth || 200;
            const height = el.offsetHeight || 150;
            minX = Math.min(minX, d.position.x);
            minY = Math.min(minY, d.position.y);
            maxX = Math.max(maxX, d.position.x + width);
            maxY = Math.max(maxY, d.position.y + height);
        });
        
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        
        viewport.scale = 1;
        // Center the patch in viewport at 100%
        viewport.x = containerRect.width / 2 - centerX;
        viewport.y = containerRect.height / 2 - centerY;
        
        applyViewportTransform();
        renderAllCables();
        updateZoomIndicator();
        saveViewport();
        showToast('100% zoom');
    }
}

/**
 * Zoom and pan viewport to fit all devices on screen
 * Devices stay in place; viewport adjusts to show all
 * Won't zoom past 100% (only zooms out if needed)
 */
export function fitAllDevices() {
    const devices = instanceModel.getAll();
    if (devices.length === 0) return;
    
    const container = document.querySelector('.canvas-container');
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    // Calculate bounding rect of all devices (need actual DOM dimensions)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    devices.forEach(d => {
        const el = document.getElementById(d.id);
        if (!el) return;
        
        const width = el.offsetWidth || 200;
        const height = el.offsetHeight || 150;
        
        minX = Math.min(minX, d.position.x);
        minY = Math.min(minY, d.position.y);
        maxX = Math.max(maxX, d.position.x + width);
        maxY = Math.max(maxY, d.position.y + height);
    });
    
    if (minX === Infinity) {
        // No valid devices found
        resetZoom();
        return;
    }
    
    // Calculate center of all devices (in canvas coordinates)
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    // Calculate patch dimensions
    const patchWidth = maxX - minX;
    const patchHeight = maxY - minY;
    
    // Calculate zoom to fit with padding (10% padding on each side)
    const padding = 0.1;
    const availableWidth = containerWidth * (1 - 2 * padding);
    const availableHeight = containerHeight * (1 - 2 * padding);
    
    const scaleX = availableWidth / patchWidth;
    const scaleY = availableHeight / patchHeight;
    const newScale = Math.min(scaleX, scaleY, 1); // Don't zoom in past 100%
    
    // Clamp scale to valid range
    viewport.scale = Math.max(viewport.minScale, Math.min(viewport.maxScale, newScale));
    
    // Pan so the center of all devices is in the center of the viewport
    // viewport.x/y is the offset applied to canvas origin
    // To center (centerX, centerY) in the viewport:
    // screenCenter = canvasCenter * scale + viewportOffset
    // containerWidth/2 = centerX * scale + viewport.x
    // viewport.x = containerWidth/2 - centerX * scale
    viewport.x = containerWidth / 2 - centerX * viewport.scale;
    viewport.y = containerHeight / 2 - centerY * viewport.scale;
    
    applyViewportTransform();
    renderAllCables();
    updateZoomIndicator();
    saveViewport();
    
    showToast('Fit to screen');
}
